import type { IdempotencySender, PendingKey, RandomSource } from "./types";

const HEADER = "Idempotency-Key";

const MAX_PENDING = 16;

const pending = new Map<string, PendingKey>();

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

export const mintIdempotencyKey = (
  source: RandomSource | undefined = (globalThis as { crypto?: RandomSource }).crypto
): string => {
  if (source && typeof source.randomUUID === "function") {
    return source.randomUUID();
  }
  if (source && typeof source.getRandomValues === "function") {
    return toHex(source.getRandomValues(new Uint8Array(16)));
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}-${Math.random()
    .toString(36)
    .slice(2, 14)}`;
};

const statusOf = (outcome: unknown): number | undefined => {
  if (typeof Response !== "undefined" && outcome instanceof Response) {
    return outcome.status;
  }
  if (outcome && typeof outcome === "object" && "status" in outcome) {
    const status = (outcome as { status?: unknown }).status;
    if (typeof status === "number") return status;
  }
  return undefined;
};

const settledOnResolve = (result: unknown): boolean => {
  const status = statusOf(result);
  return status === undefined || status < 500;
};

const settledOnReject = (error: unknown): boolean => {
  const status = statusOf(error);
  return status !== undefined && status < 500;
};

export const withIdempotencyKey = async <T>(
  url: string,
  body: string,
  send: IdempotencySender<T>
): Promise<T> => {
  const signature = `${url}\n${body}`;

  let entry = pending.get(signature);
  if (!entry) {
    entry = { key: mintIdempotencyKey(), inFlight: 0, keep: false };
    if (pending.size >= MAX_PENDING) {
      const oldest = pending.keys().next().value;
      if (oldest !== undefined) pending.delete(oldest);
    }
    pending.set(signature, entry);
  } else if (entry.inFlight === 0) {
    entry.keep = false;
  }
  entry.inFlight += 1;

  const held = entry;
  const settle = (settled: boolean) => {
    held.inFlight -= 1;
    if (!settled) held.keep = true;
    if (held.inFlight === 0 && !held.keep) pending.delete(signature);
  };

  try {
    const result = await send({ [HEADER]: held.key });
    settle(settledOnResolve(result));
    return result;
  } catch (error) {
    settle(settledOnReject(error));
    throw error;
  }
};

export const resetIdempotencyKeys = (): void => {
  pending.clear();
};
